import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { userService } from '@/_services';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    SidebarMenuSub,
    SidebarMenuSubItem,
    SidebarMenuSubButton,
    SidebarGroup,
    SidebarGroupContent,
    SidebarRail,
    useSidebar,
} from '@/components/ui/sidebar';
import {
    LayoutDashboard,
    Building2,
    BarChart3,
    FileText,
    Filter,
    Settings,
    CreditCard,
    Shield,
    LogOut,
    ChevronDown,
    X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

// Get user initials from username
const getUserInitials = (username) => {
    if (!username) return '--';
    const spaceSplit = username.split(' ');
    const hyphenSplit = username.split('-');
    if (spaceSplit.length > 1) {
        return (spaceSplit[0].charAt(0) + spaceSplit[1].charAt(0)).toUpperCase();
    } else if (hyphenSplit.length > 1) {
        return (hyphenSplit[0].charAt(0) + hyphenSplit[1].charAt(0)).toUpperCase();
    }
    return username.slice(0, 2).toUpperCase();
};

// Get compliance URL based on domain and user
const getComplianceUrl = (userData) => {
    const host = window.location.host.toLowerCase();
    
    if (host === "reporting.nulifespanrx.com" || 
        host === "reporting.rxhacker.com" || 
        host === "tempfilter.23byt.es") {
        return "https://nulifespanrx.com/compliance";
    }
    
    if (userData?.wholesaler_Id === 61) {
        return "https://nulifespanrx.com/compliance";
    }
    
    if (host === "reporting.glichealth.com" || host === "staging.glichealth.com") {
        return "https://www.glichealth.com/compliance-resources";
    }
    
    return "https://dev-web.glichealth.com/compliance-resources";
};

// Menu items configuration
const getMenuItems = (userRole, isFinanceUser, analyticsEnabled, apiSignup, enrollmentCenterSignup, clientAccessLinkSignup) => {
    const items = [];

    // Transactions (Dashboard) - All roles
    items.push({
        id: 'dashboard',
        label: 'Transactions',
        icon: LayoutDashboard,
        path: '/dashboard',
    });

    // Company Overview - Admin only
    if (userRole === 'Admin') {
        items.push({
            id: 'company-overview',
            label: 'Company Overview',
            icon: Building2,
            path: '/admin/companyOverview',
        });
    }

    // Analytics - Admin and Wholesaler
    if (userRole === 'Wholesaler' || (userRole === 'Admin' && analyticsEnabled)) {
        const analyticsSubmenu = [];
        
        if (userRole === 'Wholesaler') {
            // Only show Signups if at least one signup type is enabled
            if (apiSignup || enrollmentCenterSignup || clientAccessLinkSignup) {
                analyticsSubmenu.push({ label: 'Signups', path: '/wholesaler/analytics/signups' });
            }
            analyticsSubmenu.push(
                { label: 'Geographic', path: '/wholesaler/analytics/geographic' },
                { label: 'Pharmacies', path: '/wholesaler/analytics/pharmacies' }
            );
        } else if (userRole === 'Admin') {
            if (analyticsEnabled) {
                analyticsSubmenu.push({ label: 'Signups', path: '/admin/analytics/signups' });
            }
            analyticsSubmenu.push(
                { label: 'Geographic', path: '/admin/analytics/geographic' },
                { label: 'Pharmacies', path: '/admin/analytics/pharmacies' },
                { label: 'Public API', path: '/admin/analytics/publicapi' }
            );
        }

        items.push({
            id: 'analytics',
            label: 'Analytics',
            icon: BarChart3,
            submenu: analyticsSubmenu,
        });
    }

    // Reports - Wholesaler only (NOT Distributor/Broker, Admin, SubAdmin)
    // Condition: NOT (Distributor OR Admin OR SubAdmin)
    if (userRole === 'Wholesaler') {
        items.push({
            id: 'reports',
            label: 'Reports',
            icon: FileText,
            path: '/wholesaler/report',
        });
    } else if (userRole === 'Admin') {
        items.push({
            id: 'reports',
            label: 'Reports',
            icon: FileText,
            path: '/admin/report',
        });
    }

    // Filter - All roles
    items.push({
        id: 'filter',
        label: 'Filter',
        icon: Filter,
        path: '/filter',
    });

    // Tools - Role-specific paths
    // Broker users have userrole='Distributor' with isBrokerTypeUser=true flag, so they use /distributor/tools
    if (userRole === 'Wholesaler') {
        items.push({
            id: 'tools',
            label: 'Tools',
            icon: Settings,
            path: '/wholesaler/tools',
        });
    } else if (userRole === 'Distributor' || userRole === 'Broker') {
        items.push({
            id: 'tools',
            label: 'Tools',
            icon: Settings,
            path: '/distributor/tools',
        });
    } else if (userRole === 'Admin') {
        items.push({
            id: 'tools',
            label: 'Tools',
            icon: Settings,
            path: '/admin/tools',
        });
    }

    // Payments - Wholesaler Finance users only
    if (userRole === 'Wholesaler' && isFinanceUser) {
        items.push({
            id: 'payments',
            label: 'Payments',
            icon: CreditCard,
            path: '/wholesaler/payment',
        });
    }

    // Whitelabel Setup removed from sidebar — accessible via Tools > Whitelabel Setup

    // Compliance Resources - All roles
    items.push({
        id: 'compliance',
        label: 'Compliance Resources',
        icon: Shield,
        external: true,
    });

    return items;
};

export function AppSidebar({ ...props }) {
    const location = useLocation();
    const { open: sidebarOpen, setOpen: setSidebarOpen, isMobile, setOpenMobile } = useSidebar();
    const [userData, setUserData] = useState(null);
    const [isFinanceUser, setIsFinanceUser] = useState(false);
    const [analyticsOpen, setAnalyticsOpen] = useState(false);
    const [apiSignup, setApiSignup] = useState(false);
    const [enrollmentCenterSignup, setEnrollmentCenterSignup] = useState(false);
    const [clientAccessLinkSignup, setClientAccessLinkSignup] = useState(false);

    useEffect(() => {
        try {
            // Check both localStorage (remember me) and sessionStorage (current session)
            const userStorage = localStorage.getItem('user') || sessionStorage.getItem('user');
            const user = userStorage ? JSON.parse(userStorage) : null;
            setUserData(user);
            const financeStatus = localStorage.getItem('x_resu_t_p') || sessionStorage.getItem('x_resu_t_p');
            setIsFinanceUser(financeStatus === 'true');
            
            // Call API to get enrollment status for Wholesaler
            if (user?.userrole === 'Wholesaler' && user?.wholesaler_Id) {
                fetchEnrollmentStatus(user.wholesaler_Id);
            }
        } catch (error) {
        }
    }, []);
    
    const fetchEnrollmentStatus = async (wholesalerId) => {
        try {
            const response = await fetch('/api/Analytic/getWholesalers_Enrollment_API_Status', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Wh_Id: wholesalerId })
            });
            
            const data = await response.json();
            
            if (data && data.length > 0) {
                setApiSignup(data[1]?.colValue || false);
                setEnrollmentCenterSignup(data[2]?.colValue || false);
                setClientAccessLinkSignup(data[3]?.colValue || false);
            }
        } catch (error) {
        }
    };

    // Auto-open analytics submenu if on an analytics page
    useEffect(() => {
        if (location.pathname.includes('/analytics/')) {
            setAnalyticsOpen(true);
        }
    }, [location.pathname]);

    const userRole = userData?.isBrokerTypeUser ? 'Broker' : userData?.userrole;
    const menuItems = getMenuItems(userRole, isFinanceUser, userData?.isForAnalytic, apiSignup, enrollmentCenterSignup, clientAccessLinkSignup);
    const complianceUrl = getComplianceUrl(userData);

    const handleLogout = () => {
        userService.logout();
        localStorage.removeItem('user');
        if (window.revokecookieconsent) window.revokecookieconsent();
        if (window.xl) window.xl();
        window.location.href = window.location.origin;
    };

    const isActiveRoute = (path) => {
        if (!path) return false;
        // Treat root "/" as equivalent to "/dashboard" (both render HomePage)
        if (path === '/dashboard' && (location.pathname === '/' || location.pathname === '')) {
            return true;
        }
        // Whitelabel Setup is opened from Tools — keep Tools highlighted on that page
        if (path === '/wholesaler/tools' && location.pathname.startsWith('/wholesaler/whitelabel-setup')) {
            return true;
        }
        return location.pathname === path || location.pathname === path + '/';
    };

    const isSubmenuActive = (submenu) => {
        if (!submenu) return false;
        return submenu.some(item => isActiveRoute(item.path));
    };

    // Handle Analytics menu click - temporarily expand sidebar if collapsed (desktop only)
    const handleAnalyticsToggle = (newOpenState) => {
        const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;
        if (!sidebarOpen && !isMobileView) {
            // If sidebar is collapsed on desktop, expand it first
            setSidebarOpen(true);
            // And open the analytics menu
            setAnalyticsOpen(true);
        } else {
            // On mobile or when sidebar is already open, toggle analytics menu normally
            setAnalyticsOpen(newOpenState);
        }
    };

    // Close analytics menu when sidebar is collapsed (desktop only)
    React.useEffect(() => {
        const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;
        if (!sidebarOpen && !isMobileView) {
            setAnalyticsOpen(false);
        }
    }, [sidebarOpen]);

    const userInitials = getUserInitials(userData?.username);

    return (
        <Sidebar collapsible="icon" {...props}>
            {/* Mobile-only user profile header */}
            {isMobile && userData && (
                <SidebarHeader className="p-0">
                    <div
                        className="flex items-start gap-3 px-4 pt-4 pb-4 bg-primary"
                    >
                        {/* Circular avatar with initials */}
                        <div
                            className="flex items-center justify-center rounded-full shrink-0 text-sm font-bold mt-0.5 w-[2.75rem] h-[2.75rem] bg-white/25 text-white border-2 border-white/50"
                        >
                            {userInitials}
                        </div>

                        {/* Username and role */}
                        <div className="flex flex-col min-w-0 flex-1 pt-0.5">
                            <span
                                className="text-sm font-semibold leading-tight truncate text-white"
                            >
                                {userData.username}
                            </span>
                            <span
                                className="text-xs leading-tight mt-1 text-white/80"
                            >
                                {userRole}
                            </span>
                        </div>

                        {/* Close button */}
                        <button
                            onClick={() => setOpenMobile(false)}
                            className="shrink-0 flex items-center justify-center rounded-full transition-colors w-7 h-7 text-white bg-white/15"
                            aria-label="Close sidebar"
                        >
                            <X size={15} strokeWidth={2.5} />
                        </button>
                    </div>
                </SidebarHeader>
            )}

            {/* Menu Content */}
            <SidebarContent className="pt-4">
                <SidebarGroup>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {menuItems.map((item) => {
                                const Icon = item.icon;
                                
                                // External link (Compliance)
                                if (item.external) {
                                    return (
                                        <SidebarMenuItem key={item.id}>
                                            <SidebarMenuButton asChild tooltip={item.label}>
                                                <a
                                                    href={complianceUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <Icon />
                                                    <span>{item.label}</span>
                                                </a>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    );
                                }

                                // Submenu (Analytics)
                                if (item.submenu) {
                                    return (
                                        <Collapsible
                                            key={item.id}
                                            asChild
                                            open={analyticsOpen}
                                            onOpenChange={handleAnalyticsToggle}
                                            className="group/collapsible"
                                        >
                                            <SidebarMenuItem>
                                                <CollapsibleTrigger asChild>
                                                    <SidebarMenuButton 
                                                        tooltip={item.label}
                                                        isActive={isSubmenuActive(item.submenu)}
                                                    >
                                                        <Icon />
                                                        <span>{item.label}</span>
                                                        <ChevronDown className={cn(
                                                            "ml-auto transition-transform duration-200",
                                                            analyticsOpen && "rotate-180"
                                                        )} />
                                                    </SidebarMenuButton>
                                                </CollapsibleTrigger>
                                                <CollapsibleContent>
                                                    <SidebarMenuSub>
                                                        {item.submenu.map((subItem) => (
                                                            <SidebarMenuSubItem key={subItem.path}>
                                                                <SidebarMenuSubButton 
                                                                    asChild
                                                                    isActive={isActiveRoute(subItem.path)}
                                                                >
                                                                    <NavLink to={subItem.path}>
                                                                        <span>{subItem.label}</span>
                                                                    </NavLink>
                                                                </SidebarMenuSubButton>
                                                            </SidebarMenuSubItem>
                                                        ))}
                                                    </SidebarMenuSub>
                                                </CollapsibleContent>
                                            </SidebarMenuItem>
                                        </Collapsible>
                                    );
                                }

                                // Regular menu item
                                return (
                                    <SidebarMenuItem key={item.id}>
                                        <SidebarMenuButton 
                                            asChild 
                                            tooltip={item.label}
                                            isActive={isActiveRoute(item.path)}
                                        >
                                            <NavLink to={item.path}>
                                                <Icon />
                                                <span>{item.label}</span>
                                            </NavLink>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                );
                            })}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>

            {/* Logout Footer */}
            <SidebarFooter className="border-t border-sidebar-border">
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton onClick={handleLogout} tooltip="Logout" className="cursor-pointer">
                            <LogOut />
                            <span>Logout</span>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>

            <SidebarRail />
        </Sidebar>
    );
}

export default AppSidebar;
